import { MapPin, MapPinned } from "lucide-react";
import { SITE } from "@/config/site";
import { ChapterHeading } from "@/components/ChapterHeading";
import { Reveal } from "@/components/Reveal";
import { StarPattern } from "@/components/Ornament";
import { trackEvent, withUtm } from "@/lib/analytics";
import "@/styles/theme-v2.css";

export function Venue() {
  const v = SITE.venue;
  return (
    <section id="venue" data-testid="venue-section" className="relative px-5 py-20 sm:px-8 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-6xl">
        <ChapterHeading number="07" title="The Venue" urdu="مقام" testId="venue-heading" />
        <div className="mt-14 grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div>
              <h3
                data-testid="venue-name"
                className="font-display text-3xl leading-tight text-[#F7F4EF] sm:text-4xl"
              >
                {v.name}
              </h3>
              <p data-testid="venue-address" className="mt-4 text-lg leading-relaxed text-[#C8C2B7] sm:text-xl">
                {v.area}, {v.city}
              </p>
              <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] px-4 py-2 text-sm font-semibold text-[#F3E5AB]">
                {v.capacity}
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                <a
                  href={withUtm(v.mapsUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="venue-directions-btn"
                  onClick={() => {
                    trackEvent("directions_click");
                    trackEvent("map_click");
                  }}
                  className="btn-gold inline-flex min-h-[56px] items-center justify-center gap-3 rounded-full px-10 text-base font-semibold uppercase tracking-[0.12em] text-[#0A0805] transition-[transform,box-shadow,background-position] duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65] focus-visible:ring-offset-2 focus-visible:ring-offset-[#080705] active:scale-[0.98] sm:text-lg"
                >
                  <MapPin className="h-5 w-5" aria-hidden="true" />
                  Get Directions
                </a>
                <a
                  href={withUtm(v.mapsUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="venue-view-map-btn"
                  onClick={() => trackEvent("map_click")}
                  className="inline-flex min-h-[56px] items-center justify-center gap-3 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/[0.06] px-10 text-base font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] backdrop-blur-sm transition-colors duration-300 hover:bg-[#D4AF37]/[0.14] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65] sm:text-lg"
                >
                  <MapPinned className="h-5 w-5" aria-hidden="true" />
                  View on Google Maps
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div
              data-testid="venue-map-placeholder"
              className="arch-frame relative flex aspect-[4/3] flex-col items-center justify-center overflow-hidden border border-[#D4AF37]/30 bg-[#161310] p-8 text-center"
            >
              {/* Photo-ready: renders a real venue photo the moment
                  SITE.venue.hasPhoto is flipped to true and the file exists at
                  SITE.venue.photo — until then, falls back to the existing
                  map-icon placeholder below (onError guards against a broken
                  image even if hasPhoto is ever set without the file present). */}
              {v.hasPhoto && v.photo ? (
                <img
                  src={v.photo}
                  alt={`${v.name} — venue photo`}
                  data-testid="venue-photo"
                  className="cah-kenburns absolute inset-0 h-full w-full object-cover"
                  style={{ objectPosition: "50% 22%" }}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <>
                  <StarPattern id="venue-map-pattern" opacity={0.25} />
                  <div
                    className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.12),transparent_70%)]"
                    aria-hidden="true"
                  />
                  <MapPin className="relative h-10 w-10 text-[#D4AF37]" aria-hidden="true" />
                  <p className="font-display relative mt-4 text-xl text-[#F7F4EF]">Map &amp; Directions</p>
                  <p className="relative mt-2 max-w-xs text-sm leading-relaxed text-[#918A7E] sm:text-base">
                    The interactive map embed is on its way. Use the directions button to open the venue
                    in Google Maps.
                  </p>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
